import type { AxiosResponse } from 'axios';
import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import type { PodaniChybyDto } from '@gov-nx/api';
import {
	electionsQuery,
	sendSubmissionQuery,
	usePoMutation,
	usePoQuery,
	useAxiosInstance,
} from '@gov-nx/api';
import { useExtraConfigContext } from '@gov-nx/core/app';
import {
	useCountriesQuery,
	useCountryCodes,
	useDataBoxes,
	useDownloadBlob,
	useEmbassyOfficeQuery,
	usePersonCommunicationData,
	usePersonCommunicationForm,
	usePoIndividualDataLoad,
	useServiceAuthorization,
	useWizard,
} from '@gov-nx/core/hooks';
import { getRequiredDataBoxes, useLocale } from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import { propEq } from '@gov-nx/core/types';
import type { ServiceCode } from '@gov-nx/module/service';
import { getDetailPageName, getSelectedRounds } from '@gov-nx/module/service';
import { composeAddress } from '@gov-nx/utils/common';
import { FormStep1 } from './FormStep1';
import { FormStep2 } from './FormStep2';
import { FormStep2a } from './FormStep2a';
import { FormStep2b } from './FormStep2b';
import { FormStep3 } from './FormStep3';
import { FormStep4 } from './FormStep4';
import type { VotersCardFormData, VotersCardContext } from './context.types';
import {
	electionRoundDefaultValues,
	prepareCheckData,
	prepareSubmitData,
	prepareToDownloadPowerOfAttorney,
} from './formDefinitions';
import { useInitialCheck } from './useInitialCheck';
import {
	canVoteFromAbroad,
	isTwoRoundedElection,
	transformControl,
} from './utils';
import { useAutorizaceContext } from 'libs/component/web/src/Autorizace/Context';

const VolicskyPrukazDetailContext =
	createContext<Nullable<VotersCardContext>>(null);

interface VolicskyPrukazDetailContextProviderProps {
	children: ReactNode;
	code: ServiceCode;
	electionId: number;
	onSubmitted: () => void;
	platform: 'web' | 'native';
}

const AUTHORIZATION_SERVICE_NAME = 'PODANI_ZADOST_VOLEBNI_PRUKAZ';

export const VolicskyPrukazDetailContextProvider = ({
	children,
	code,
	electionId,
	onSubmitted,
	platform,
}: VolicskyPrukazDetailContextProviderProps) => {
	const { t } = useLocale(code);
	const { handleDownloadBlob } = useDownloadBlob();
	const {
		extraConfig: { application },
	} = useExtraConfigContext();
	const { isDataBoxConnected, requiredDataBoxes } = useDataBoxes(
		getRequiredDataBoxes(code, application)
	);
	const { axiosInstance } = useAxiosInstance();
	const authContext = useAutorizaceContext();
	const { isLowAuthorization, isAuthorizationEnforced } =
		useServiceAuthorization(AUTHORIZATION_SERVICE_NAME);

	const countryCodes = useCountryCodes();

	const query = usePoQuery({
		queryKey: ['announced-elections'],
		queryFn: () => electionsQuery(axiosInstance),
	});

	const election = query.data?.seznam?.find((e) => e.volbyId === electionId);

	const individualPerson = usePoIndividualDataLoad();
	const personCommunicationData = usePersonCommunicationData();

	const embassyOfficesQuery = useEmbassyOfficeQuery();

	const countriesQuery = useCountriesQuery();

	const [isSubmitted, setIsSubmitted] = useState(false);

	const submitMutation = usePoMutation({
		mutationFn: (formData: VotersCardFormData) => {
			const prepared = prepareSubmitData(
				formData,
				electionId,
				election,
				embassy
			);
			return sendSubmissionQuery(axiosInstance)(prepared);
		},
		onSuccess: async (data) => {
			const nonHighAuthorizationFlow = () => {
				setIsSubmitted(true);
				onSubmitted();
			};

			if (authContext) {
				authContext?.onInitAuthorization(
					AUTHORIZATION_SERVICE_NAME,
					data.headers['x-autorizaceid'],
					nonHighAuthorizationFlow
				);
			} else {
				nonHighAuthorizationFlow();
			}
		},
	});

	const checkMutation = usePoMutation<
		AxiosResponse<PodaniChybyDto>,
		VotersCardFormData['electionRound']
	>({
		mutationFn: (rounds) => {
			const prepared = prepareCheckData(rounds, electionId, election);
			return sendSubmissionQuery(axiosInstance)(prepared) as unknown as Promise<
				AxiosResponse<PodaniChybyDto, VotersCardFormData['electionRound']>
			>;
		},
	});
	const checkMutationData = checkMutation.data?.data
		? transformControl(checkMutation.data.data, t)
		: undefined;

	const rounds = election?.kola ?? [];

	const initialCheck = useInitialCheck({
		election,
		electionId,
		rounds: electionRoundDefaultValues(rounds),
	});

	const formStep1 = FormStep1({
		code,
		rounds,
		isProcessing: checkMutation.isLoading,
		isDisabled: checkMutationData?.errors
			? checkMutationData?.errors?.length > 0
			: false,
	});

	const { embassyOfficesOptions } = embassyOfficesQuery;

	const formStep2 = FormStep2({
		code,
		embassyOfficesOptions,
		isProcessing: checkMutation.isLoading,
	});
	const formStep2a = FormStep2a({
		code,
		embassyOfficesOptions,
		isProcessing: checkMutation.isLoading,
	});
	const formStep2b = FormStep2b({
		code,
		isProcessing: checkMutation.isLoading,
	});
	const formStep3 = FormStep3({
		code,
		embassyOfficesOptions,
		checkMutationData,
		election,
		countryOptions: countriesQuery.countryOptions,
	});
	const formStep4 = FormStep4({
		dataBoxTypes: requiredDataBoxes,
		isLowAuthorization,
	});

	const isOneRoundedElection = election
		? !isTwoRoundedElection(election.druh)
		: false;

	const checkCall = async (): Promise<{ canContinue: boolean }> => {
		const electionRound =
			formStep1.formDefinition.formMethods.getValues('electionRound');

		const response = await checkMutation.mutateAsync(electionRound);
		const transformed = transformControl(response.data, t);
		if (transformed.errors.length > 0) {
			return { canContinue: false };
		}

		return { canContinue: true };
	};

	const wizard = useWizard<VotersCardFormData>({
		steps:
			platform === 'native'
				? [
						formStep1.formDefinition,
						formStep2a.formDefinition,
						formStep2b.formDefinition,
						formStep3.formDefinition,
						formStep4,
					]
				: [
						formStep1.formDefinition,
						formStep2.formDefinition,
						formStep3.formDefinition,
						formStep4,
					],
		canInitialize: !!election,
		initialIndexOpen: isOneRoundedElection ? [1] : undefined,
		onStepSubmitted: async (index) => {
			if (isOneRoundedElection && index === 1) {
				return checkCall();
			}
			if (index === 0) {
				return checkCall();
			}
			return { canContinue: true };
		},
		onStepInvalid: (index, errors) => {
			if (platform === 'native' ? index === 2 : index === 1) {
				personCommunicationData.onFormValidated(errors);
			}
		},
		onSubmit: async (formData) => submitMutation.mutate(formData),
	});

	const embassyCode =
		platform === 'native'
			? formStep2a.formDefinition.formMethods.watch('embassyCode')
			: formStep2.formDefinition.formMethods.watch('embassyCode');

	const embassy = embassyCode
		? embassyOfficesQuery.data?.seznam?.find(propEq('kod', embassyCode))
		: undefined;

	usePersonCommunicationForm(formStep2.formDefinition, {
		countryCodes,
		email: personCommunicationData.emailVerified,
		phone: personCommunicationData.phoneVerified,
	});

	usePersonCommunicationForm(formStep2b.formDefinition, {
		countryCodes,
		email: personCommunicationData.emailVerified,
		phone: personCommunicationData.phoneVerified,
	});

	useEffect(() => {
		if (
			countriesQuery.countryOptions &&
			!formStep3.formDefinition.formMethods.getValues('stat')
		) {
			formStep3.formDefinition.formMethods.setValue('stat', '203'); // czech code
		}
	}, [countriesQuery.countryOptions, formStep3.formDefinition.formMethods]);

	const selectedRounds = getSelectedRounds(
		formStep1.formDefinition.formMethods.getValues('electionRound'),
		formStep1.fields.electionRound
	);

	const livesAbroad =
		platform === 'native'
			? formStep2a.formDefinition.formMethods.watch('livesAbroad')
			: formStep2.formDefinition.formMethods.watch('livesAbroad');

	const onSpecialList =
		platform === 'native'
			? formStep2a.formDefinition.formMethods.watch('onSpecialList')
			: formStep2.formDefinition.formMethods.watch('onSpecialList');

	const volitNaOkrskuVZahraniciKod = formStep3.formDefinition.formMethods.watch(
		'volitNaOkrskuVZahraniciKod'
	);

	return (
		<VolicskyPrukazDetailContext.Provider
			value={{
				code,
				initialState: {
					isLoading:
						query.isLoading ||
						individualPerson.isLoading ||
						countriesQuery.isLoading ||
						embassyOfficesQuery.isLoading ||
						!countryCodes.isFetched ||
						initialCheck.isLoading,
					error: (query.error || embassyOfficesQuery.error) ?? undefined,
				},
				checkMutation: {
					isLoading: checkMutation.isLoading,
					error: checkMutation.error ?? undefined,
					data: checkMutationData,
				},
				submitMutation: {
					isSubmitting: submitMutation.isLoading,
					isSubmitted,
				},
				election,
				requiredDataBoxes,
				isDataBoxConnected,
				wizard,
				personCommunicationData,
				individualPerson: individualPerson.individualPerson,
				formStep1: formStep1,
				formStep2: formStep2,
				formStep2a: formStep2a,
				formStep2b: formStep2b,
				formStep3: formStep3,
				livesAbroad,
				onSpecialList,
				zpusobPrevzeti:
					formStep3.formDefinition.formMethods.watch('zpusobPrevzeti'),
				zpusobPrevzetiPosta: formStep3.formDefinition.formMethods.watch(
					'zpusobPrevzetiPosta'
				),
				zpusobPrevzetiUrad:
					formStep3.formDefinition.formMethods.watch('zpusobPrevzetiUrad'),
				embassy,
				volitNaOkrskuVZahranici: (embassyOfficesQuery.data?.seznam ?? []).find(
					(embassy) => embassy.kod === volitNaOkrskuVZahraniciKod
				),
				formData: wizard.formData,
				canVoteFromAbroad: election ? canVoteFromAbroad(election.druh) : false,
				addressOtherThanPermanentResidence: [
					wizard.formData.obec,
					wizard.formData.cislo,
					composeAddress(wizard.formData.ulice, wizard.formData.cisloUlice),
					countriesQuery.countryOptions.find(
						(country) => country.value === wizard.formData.stat
					)?.label,
				].join(', '),
				detailName: getDetailPageName(t, election),
				countryOptions: countriesQuery.countryOptions,
				powerOfAttorney: handleDownloadBlob(() =>
					prepareToDownloadPowerOfAttorney(electionId, wizard.formData)
				),
				selectedRounds,
				initialCheck,
				isLowAuthorization,
				isAuthorizationEnforced,
			}}>
			{children}
		</VolicskyPrukazDetailContext.Provider>
	);
};

export const useVolicskyPrukazDetailContext = (): VotersCardContext =>
	useContext(VolicskyPrukazDetailContext) as VotersCardContext;
